/**
 * Keeping a caret in place when someone else's typing changes the text under
 * it (the script panel, PLAN §6.2 "Two people edit the same script"): the
 * change is located as the span between the common prefix and suffix of the
 * old and new text, and a caret after that span shifts by the length
 * difference; one inside it moves to the span's new end. Pure.
 */

export type Selection = { readonly start: number; readonly end: number };

function commonPrefix(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
}

function commonSuffix(a: string, b: string, prefix: number): number {
  let i = 0;
  while (i < a.length - prefix && i < b.length - prefix && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
  return i;
}

function mapOffset(offset: number, prefix: number, oldEnd: number, delta: number): number {
  if (offset <= prefix) return offset;
  if (offset >= oldEnd) return offset + delta;
  return oldEnd + delta;
}

/**
 * The user's edit `prev → next`, replayed onto `current`.
 *
 * The textarea's input event reports the text the user typed into (`prev`,
 * what the DOM showed) and the result (`next`). If someone else's typing has
 * reached the store but not yet the DOM, `current` is newer than `prev`:
 * diffing `current → next` would read their characters as deleted by us
 * (found by the two-user script e2e test). Instead the one span the user
 * changed is located in `prev`, mapped into `current` by `transformSelection`,
 * and applied there.
 */
export function rebaseEdit(prev: string, next: string, current: string): string {
  if (current === prev) return next;
  const prefix = commonPrefix(prev, next);
  const suffix = commonSuffix(prev, next, prefix);
  const inserted = next.slice(prefix, next.length - suffix);
  const span = transformSelection(prev, current, { start: prefix, end: prev.length - suffix });
  return `${current.slice(0, span.start)}${inserted}${current.slice(Math.max(span.start, span.end))}`;
}

/** Where `selection` in `before` belongs in `after`. */
export function transformSelection(before: string, after: string, selection: Selection): Selection {
  if (before === after) return selection;
  const prefix = commonPrefix(before, after);
  const suffix = commonSuffix(before, after, prefix);
  const oldEnd = before.length - suffix;
  const delta = after.length - before.length;
  return { start: mapOffset(selection.start, prefix, oldEnd, delta), end: mapOffset(selection.end, prefix, oldEnd, delta) };
}
